import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { BackToDashboard } from "../components/BackToDashboard";
import { Mail, Save, Send } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const TRIGGERS = [
  { k: "status_change",        label: "Any permit status change" },
  { k: "permit_approved",      label: "Permit approved" },
  { k: "permit_denied",        label: "Permit denied" },
  { k: "permit_delayed",       label: "Permit delayed (stuck in review)" },
  { k: "permit_expiring",      label: "Permit expiring soon" },
  { k: "inspection_scheduled", label: "Inspection scheduled" },
  { k: "reminder_24h",         label: "24h inspection reminder" },
  { k: "reminder_48h",         label: "48h inspection reminder" },
  { k: "filing_submitted",     label: "Filing submitted / pre-filed",           group: "Plan Filings" },
  { k: "filing_assigned",      label: "Filing assigned to plan examiner",        group: "Plan Filings" },
  { k: "plan_approved",        label: "Plans approved by DOB",                   group: "Plan Filings" },
  { k: "plan_disapproved",     label: "Plans disapproved (objections)",          group: "Plan Filings" },
  { k: "violation_issued",     label: "DOB violation issued",                    group: "Violations" },
  { k: "stop_work_order",      label: "Stop-Work Order issued ðŸ›‘",               group: "Violations" },
  { k: "ecb_violation",        label: "ECB (Environmental Control Board) violation", group: "Violations" },
  { k: "hpd_violation",        label: "HPD formal violation",                    group: "Violations" },
];

export default function AlertsSettings() {
  const [s, setS]                             = useState(null);
  const [savedS, setSavedS]                   = useState(null);
  const [saving, setSaving]                   = useState(false);

  useEffect(() => {
    api.get("/alerts/settings")
      .then(r => {
        const d = r.data;
        const settings = (d && typeof d === "object" && d.triggers && d.channels)
          ? d
          : { triggers: {}, channels: { email: true } };
        settings.channels = { email: settings.channels?.email !== false };
        setS(settings);
        setSavedS(settings);
      })
      .catch(() => {
        const fallback = { triggers: {}, channels: { email: true } };
        setS(fallback);
        setSavedS(fallback);
      });
  }, []);

  if (!s) return <AlertsSettingsSkeleton />;

  const isDirty = savedS && JSON.stringify(s) !== JSON.stringify(savedS);

  const toggleTrigger = (k) =>
    setS(p => ({ ...p, triggers: { ...p.triggers, [k]: !p.triggers[k] } }));

  const toggleChannel = (k) =>
    setS(p => ({ ...p, channels: { email: k === "email" ? !p.channels.email : p.channels.email } }));

  const save = async () => {
    setSaving(true);
    const channels = { email: !!s.channels.email };
    try {
      const res = await api.put("/alerts/settings", { triggers: s.triggers, channels });
      const saved = res.data || { ...s, channels };
      setS(saved);
      setSavedS(saved);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    const id = toast.loading("Sending test alertâ€¦");
    try {
      const r = await api.post("/alerts/test");
      const { email } = r.data;
      if (email.disabled_by_settings) {
        toast.error("Email alerts are disabled in your settings. Enable the Email channel and save first.", { id });
      } else if (email.delivered) {
        toast.success(`Test email sent to ${email.to}. Check your inbox.`, { id });
      } else if (email.attempted) {
        toast.error("Email delivery failed. Check your Resend API key in Railway.", { id });
      } else {
        toast.error("No email address on file for this account.", { id });
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Daily limit of 10 test alerts reached. Try again tomorrow.", { id });
      } else {
        toast.error("Test alert failed.", { id });
      }
    }
  };

  const groupedTriggers = TRIGGERS.reduce((acc, t) => {
    const g = t.group || "Permits & Inspections";
    if (!acc[g]) acc[g] = [];
    acc[g].push(t);
    return acc;
  }, {});

  return (
    <>
      {/* Sticky unsaved-changes banner */}
      {isDirty && (
        <div className="sticky top-0 z-50 w-full flex items-center justify-between gap-3 px-4 sm:px-8 py-3 bg-amber-500 text-zinc-950 shadow-md">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <span className="inline-block w-2 h-2 rounded-full bg-zinc-950/30 shrink-0" />
            You have unsaved changes to your alert settings.
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 bg-zinc-950 text-white px-4 py-1.5 rounded-sm text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50 shrink-0"
          >
            <Save size={13} /> {saving ? "Savingâ€¦" : "Save now"}
          </button>
        </div>
      )}

      <div className="px-4 sm:px-8 py-8 max-w-3xl mx-auto" data-testid="alert-settings-page">
        <BackToDashboard />
        <span className="label-eyebrow">Notifications</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-2">Alert Settings</h1>
        <p className="text-sm text-zinc-600 mb-4">Choose which updates should send an email alert.</p>

        {/* Delivery Channels */}
        <div className="border border-zinc-200 bg-white rounded-sm p-5 mb-4">
          <div className="label-eyebrow mb-3">Delivery Channels</div>
          <div className="grid grid-cols-1 gap-3">
            <div
              className="flex items-center justify-between px-4 py-4 border border-zinc-200 rounded-sm cursor-pointer min-h-[56px] active:bg-zinc-50"
              onClick={() => toggleChannel("email")}
              data-testid="channel-email"
            >
              <span className="inline-flex items-center gap-2 text-sm font-medium select-none">
                <Mail size={14} className="text-zinc-500" /> Email
              </span>
              <Switch
                checked={!!s.channels.email}
                className="pointer-events-none"
              />
            </div>
          </div>
        </div>

        {/* Notification Triggers */}
        {Object.entries(groupedTriggers).map(([group, items]) => (
          <div key={group} className="border border-zinc-200 bg-white rounded-sm divide-y divide-zinc-100 mb-4">
            <div className="px-5 py-3 label-eyebrow">{group}</div>
            {items.map(t => (
              <div
                key={t.k}
                className="flex items-center justify-between px-5 py-4 cursor-pointer min-h-[52px] active:bg-zinc-50"
                onClick={() => toggleTrigger(t.k)}
                data-testid={`trigger-${t.k}`}
              >
                <div className="text-sm select-none">{t.label}</div>
                <Switch
                  checked={!!s.triggers[t.k]}
                  className="pointer-events-none"
                  data-testid={`trigger-switch-${t.k}`}
                />
              </div>
            ))}
          </div>
        ))}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mt-6">
          <div className="flex flex-col gap-1">
            <button
              onClick={sendTest}
              data-testid="send-test-alert"
              className="inline-flex items-center justify-center gap-2 bg-white border border-zinc-200 px-5 py-2.5 rounded-sm text-sm font-semibold hover:bg-zinc-50"
            >
              <Send size={14} /> Send test alert to me
            </button>
            <span className="text-xs text-center text-zinc-400">Limit: 10 test alerts per day</span>
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-alerts-button"
            className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-sm text-sm font-semibold disabled:opacity-50 ${
              isDirty
                ? "bg-amber-500 hover:bg-amber-600 text-white"
                : "bg-zinc-950 hover:bg-zinc-800 text-white"
            }`}
          >
            <Save size={14} /> {saving ? "Savingâ€¦" : isDirty ? "Save changes â€¢" : "Save changes"}
          </button>
        </div>
      </div>
    </>
  );
}

function AlertsSettingsSkeleton() {
  return (
    <div className="px-4 sm:px-8 py-8 max-w-3xl mx-auto" data-testid="alert-settings-skeleton">
      <Skeleton className="h-4 w-28 mb-5" />
      <Skeleton className="h-4 w-28 mb-3" />
      <Skeleton className="h-10 w-72 mb-2" />
      <Skeleton className="h-4 w-96 max-w-full mb-6" />
      <div className="border border-zinc-200 bg-white rounded-sm p-5 mb-4">
        <Skeleton className="h-4 w-36 mb-4" />
        <div className="grid grid-cols-1 gap-3">
          {[...Array(1)].map((_, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-4 border border-zinc-200 rounded-sm">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-20" />
              </div>
              <Skeleton className="h-6 w-11 rounded-full" />
            </div>
          ))}
        </div>
      </div>
      {[...Array(3)].map((_, group) => (
        <div key={group} className="border border-zinc-200 bg-white rounded-sm divide-y divide-zinc-100 mb-4">
          <div className="px-5 py-3"><Skeleton className="h-4 w-40" /></div>
          {[...Array(group === 0 ? 5 : 3)].map((_, i) => (
            <div key={i} className="flex items-center justify-between px-5 py-4 min-h-[52px]">
              <Skeleton className="h-4 w-64 max-w-[70%]" />
              <Skeleton className="h-6 w-11 rounded-full" />
            </div>
          ))}
        </div>
      ))}
      <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mt-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-10 w-40" />
      </div>
    </div>
  );
}
